import React from "react";
import { useTranslation } from "react-i18next";
import { Typography, theme } from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { Request, StatusKind } from "../api";

const { Text } = Typography;

/**
 * The identity band at the top of a case.
 *
 * It replaces a single `<h2>View: {number} | <Tag/></h2>`, which put four
 * different kinds of information — a verb, a name, a separator and a state — on
 * one line at one weight, so nothing was findable at a glance.
 *
 * Three tiers, most identifying first:
 *
 *   1. the process this case belongs to, quiet and small;
 *   2. the case number, which is what people say out loud and search for, set
 *      large and in tabular figures so a column of them lines up;
 *   3. the facts that decide what to do next — where it is in its lifecycle,
 *      when it was opened, by whom, when the work runs, where.
 *
 * "View:" is gone on purpose. The page is the view; the word was labelling the
 * screen rather than the case, and it pushed the number — the only text here
 * anyone ever reads out — into second place.
 */

/**
 * Lifecycle colour comes from `status.kind`, never from `status.name`.
 *
 * Clients rename statuses into Russian and Kazakh; the kind is the part that
 * doesn't move. Keying colour off names is the exact mistake that has already
 * caused three production bugs elsewhere in this codebase.
 */
const KIND_TONE: Record<StatusKind, "neutral" | "info" | "warning" | "success"> = {
  Draft: "neutral",
  DataEntry: "info",
  Approval: "warning",
  Active: "success",
  Closed: "neutral",
  Other: "neutral",
};

type Props = {
  request: Request;
  /** Rendered at the right of the band — in practice the Actions dropdown. */
  actions?: React.ReactNode;
};

const RequestHeader: React.FC<Props> = ({ request, actions }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const kind = (request.status?.kind ?? "Other") as StatusKind;
  const tone = KIND_TONE[kind] ?? "neutral";

  const toneColors: Record<string, { fg: string; bg: string; dot: string }> = {
    neutral: {
      fg: token.colorTextSecondary,
      bg: token.colorFillQuaternary,
      dot: token.colorTextQuaternary,
    },
    info: { fg: token.colorInfoText, bg: token.colorInfoBg, dot: token.colorInfo },
    warning: {
      fg: token.colorWarningText,
      bg: token.colorWarningBg,
      dot: token.colorWarning,
    },
    success: {
      fg: token.colorSuccessText,
      bg: token.colorSuccessBg,
      dot: token.colorSuccess,
    },
  };
  const colors = toneColors[tone];

  /** "17 Aug 2026, 14:03" in whichever language the user is reading. */
  const formatDateTime = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(i18n.language, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const created = formatDateTime(request.created_at);
  const from = formatDate(request.start_date);
  const to = formatDate(request.end_date);
  const validity = from && to ? `${from} — ${to}` : from ?? to ?? null;

  /**
   * One meta item. Every one is optional: most of these columns are nullable,
   * and a label with an em dash after it is noise, so an absent fact simply
   * isn't drawn rather than being drawn as empty.
   */
  const Meta: React.FC<{
    icon: React.ReactNode;
    label: string;
    value?: React.ReactNode;
    title?: string;
  }> = ({ icon, label, value, title }) => {
    if (!value) return null;
    return (
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: token.colorTextTertiary,
            lineHeight: 1.4,
          }}
        >
          {label}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 2,
            fontSize: 13,
            color: token.colorText,
            minWidth: 0,
          }}
          title={title}
        >
          <span style={{ color: token.colorTextQuaternary, flexShrink: 0 }}>
            {icon}
          </span>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </span>
        </div>
      </div>
    );
  };

  const metaItems = [
    <Meta
      key="created"
      icon={<ClockCircleOutlined />}
      label={t("Created")}
      value={created}
    />,
    <Meta
      key="by"
      icon={<UserOutlined />}
      label={t("Requested by")}
      value={request.created_by_name}
      title={request.created_by_name}
    />,
    <Meta
      key="validity"
      icon={<CalendarOutlined />}
      label={t("Valid")}
      value={validity}
    />,
    <Meta
      key="location"
      icon={<EnvironmentOutlined />}
      label={t("Location")}
      value={request.location}
      title={request.location ?? undefined}
    />,
  ].filter((item) => item.props.value);

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          {request.template?.name && (
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: token.colorTextTertiary,
                lineHeight: 1.4,
                marginBottom: 2,
              }}
            >
              {t(request.template.name)}
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: 24,
                fontWeight: 600,
                lineHeight: 1.2,
                color: token.colorText,
                // Case numbers are read in columns and compared digit by digit.
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {request.number}
            </span>
            {request.status?.name && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "3px 10px",
                  borderRadius: 999,
                  background: colors.bg,
                  color: colors.fg,
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: colors.dot,
                    // The dot carries the same fact as the label's colour, for
                    // anyone who can't separate these hues.
                    flexShrink: 0,
                  }}
                />
                {t(request.status.name)}
              </span>
            )}
          </div>
          {/* The free-text title, when the template collects one: a number
              identifies a case, this says what it is about. */}
          {request.title && (
            <div style={{ marginTop: 4, maxWidth: 720 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {request.title}
              </Text>
            </div>
          )}
        </div>
        {actions && <div style={{ flexShrink: 0 }}>{actions}</div>}
      </div>

      {metaItems.length > 0 && (
        <>
          {/* A hairline, not a second bordered box: the meta row belongs to the
              same surface as the title it describes. */}
          <div
            style={{
              height: 1,
              background: token.colorSplit,
              margin: "14px 0 12px",
            }}
          />
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px 40px",
            }}
          >
            {metaItems}
          </div>
        </>
      )}
    </div>
  );
};

export default RequestHeader;
